import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, Router, RouterStateSnapshot, UrlTree } from '@angular/router';
import { OrganizationType } from '@portal/api/enums/organization-type';
import { Org } from '@portal/api/responses/organizations/org-details-response';
import { AppContextService } from '@portal/shared/services/app-context.service';
import { OrganizationSelectorService } from '@portal/shared/services/organization-selector.service';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

@Injectable({
    providedIn: 'root',
})
export class AppContextOrgTypeGuard  {
    constructor(
        private readonly _appContextService: AppContextService,
        private readonly _organizationSelectorService: OrganizationSelectorService,
        private readonly _router: Router,
    ) {
    }

    canActivate(_route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean | UrlTree> {
        return this.validateCurrentOrgContext(state.url);
    }

    canActivateChild(_route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean | UrlTree> {
        return this.validateCurrentOrgContext(state.url);
    }

    private validateCurrentOrgContext(url: string): Observable<boolean | UrlTree> {
        return this._organizationSelectorService.ensureSelectedOrg$().pipe(
            map(org => this.resolveCurrentOrg(org, url)),
            catchError(() => of(true))
        );
    }

    private resolveCurrentOrg(org: Org | null, url: string): boolean | UrlTree {
        const defaultRoute = this._appContextService.defaultRoute();
        const normalizedUrl = this.normalizeUrl(url);

        if (!org) {
            return normalizedUrl === defaultRoute || this.isClubDetailsRouteAllowedWithoutOrg(normalizedUrl)
                ? true
                : this._router.parseUrl(defaultRoute);
        }

        const currentOrgType = org?.type;

        if (!this.isContextAwareOrgType(currentOrgType)) {
            return true;
        }

        if (this._appContextService.isOrgTypeAllowed(currentOrgType)) {
            return true;
        }

        this._organizationSelectorService.resetSelectedOrg();
        return this._router.parseUrl(defaultRoute);
    }

    private isContextAwareOrgType(type: OrganizationType | string | number | null | undefined): boolean {
        return type === OrganizationType.Party
            || type === OrganizationType.Association
            || type === String(OrganizationType.Party)
            || type === String(OrganizationType.Association)
            || String(type).toLowerCase() === 'party'
            || String(type).toLowerCase() === 'association';
    }

    private normalizeUrl(url: string): string {
        return (url || '').split('?')[0].split('#')[0] || '/';
    }

    private isClubDetailsRouteAllowedWithoutOrg(url: string): boolean {
        try {
            return this._appContextService.getContext() === 'club'
                && url === '/organizations/details';
        } catch {
            return false;
        }
    }
}
